/**
 * Cool-Tools Corporate Identity Design System
 * Single source of truth for all brand colors, typography and spacing.
 */
export const colors = {
    primary: '#2563EB', // indigo blue
    accent: '#06B6D4', // cyan / teal
    dark: '#0F172A',
    lightBg: '#F8FAFC',
    white: '#FFFFFF',
    // Derived / semantic
    primaryHover: '#1D4ED8',
    accentHover: '#0891B2',
    surface: '#FFFFFF',
    border: '#E2E8F0',
    textPrimary: '#0F172A',
    textSecondary: '#475569',
    textMuted: '#94A3B8',
    // Status
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    successBg: '#D1FAE5',
    warningBg: '#FEF3C7',
    errorBg: '#FEE2E2',
};
export const typography = {
    fontFamily: "'Segoe UI', Inter, sans-serif",
    fontSizeXs: 11,
    fontSizeSm: 13,
    fontSizeBase: 14,
    fontSizeMd: 16,
    fontSizeLg: 20,
    fontSizeXl: 24,
    fontSizeHero: 40,
};
export const radius = {
    button: 4,
    card: 8,
    icon: 8,
};
export const spacing = {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 40,
    xxl: 64,
};
/** Linear gradient string: primary → accent (left to right) */
export const brandGradient = `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`;
/** Shared card style object for inline use */
export const cardStyle = {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.card,
    padding: spacing.lg,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};
/** Page wrapper style */
export const pageStyle = {
    fontFamily: typography.fontFamily,
    backgroundColor: colors.lightBg,
    minHeight: '100vh',
    color: colors.textPrimary,
};
// React is imported in consuming files – this import is only for the CSSProperties type.
// We keep it import-free at module level; consumers already import React.
// ── Brand constants ───────────────────────────────────────────────────────────
/** "cool·tools" rendered as styled spans – use dangerouslySetInnerHTML or build manually */
export const BRAND_NAME = 'cool·tools';
export const TAGLINE = 'Productivity Tools for Professionals';
export const TAGLINE_SHORT = 'Productivity Tools for Professionals';
// WI#4619: SUPPORT_URL / PRIVACY_URL removed -- no consumer anywhere (Grep over packages,
// extensions, api), and both pointed at pages that did not exist. The pages link to each
// other by path; the link guard in __tests__/legal-pages.test.ts checks every one.
/** Fluent UI theme palette override to match CI */
export const fluentPalette = {
    themePrimary: colors.primary,
    themeLighterAlt: '#EFF6FF',
    themeLight: '#BFDBFE',
    themeTertiary: '#93C5FD',
    themeSecondary: '#60A5FA',
    themeDarkAlt: colors.primaryHover,
    themeDark: '#1E40AF',
    themeDarker: '#1E3A8A',
    neutralLighter: '#F3F4F6',
    neutralLight: '#E5E7EB',
    neutralPrimary: colors.dark,
    white: colors.white,
};
// ── Extension-Katalog ── ENTFERNT 2026-08-26 (WI#4521) ───────────────────
//
// Hier standen das Interface ExtensionMeta und die Konstante EXTENSIONS -- ein
// Katalog aller fuenf Extensions mit Id, Name, Tagline, Preis, Kategorie,
// Icon-Pfad und Marketplace-Slug. Beide Bezeichner wurden im GESAMTEN Repo von
// nichts importiert (gemessen ueber alle .ts/.tsx/.cjs: jedes andere EXTENSIONS
// ist eine eigene lokale Konstante seiner Datei). Ihre Nachfolgerin ist
// marketing/products.ts.
//
// Entfernt statt stehengelassen, weil sie drei falsche Angaben trug und dabei
// gepflegt AUSSAH -- Massenaenderungen (Branding, URL-Ersetzung) haben sie
// mitgezogen, ihr Inhalt aber nie:
//
//   * price: 5/8/8/5/5, dokumentiert als "USD per org per month". Real sind es
//     EUR, und zwar 2,49/3,99/3,99/2,99/2,99 pro Nutzer bzw.
//     249/349/349/249/249 pro Projekt -- keine der beiden Zahlen.
//     Damit war sie eine SCHLAFENDE ACHTE PREISQUELLE: wirkungslos, solange
//     niemand sie liest, und falsch in dem Moment, in dem es jemand tut.
//     WI#4533 hat sieben Preisquellen auf zwei reduziert und genau diese
//     uebersehen, weil sie in einer Datei steht, die nach Design-Tokens
//     aussieht.
//   * iconUrl zeigte auf /images/<id>/icon.png -- das Verzeichnis
//     public/images/ existiert nicht.
//   * name in einer vierten Schreibweise, neben Manifest, Monitoren und
//     products.ts.
//
// Wiederherstellbar aus der Git-Historie. Wer hier eine Extension-Liste
// braucht: marketing/products.ts fuehrt sie, und ab WI#4529 kommt sie aus der
// generierten Ableitung.
/**
 * Returns inline CSS for the programmatic icon fallback:
 * gradient square with abbreviation text centred.
 * Used as onError handler on <img> elements.
 */
export function iconFallbackStyle(size = 64) {
    return {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: radius.icon,
        background: brandGradient,
        color: colors.white,
        fontFamily: typography.fontFamily,
        fontWeight: 700,
        fontSize: Math.round(size * 0.28),
        letterSpacing: '0.05em',
        flexShrink: 0,
        userSelect: 'none',
    };
}
